import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { IonButton, IonButtons, IonContent, IonGrid, IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { Observable } from 'rxjs';
import { Slip } from 'src/app/models/slip.model';
import { Student } from 'src/app/models/student.model';
import { ModalBackButtonComponent } from '../modal-back-button/modal-back-button.component';
import { StudentSlipConsentComponent } from './student-slip-consent/student-slip-consent.component';
import { StudentSlipDetailsComponent } from './student-slip-details/student-slip-details.component';
import { StudentSlipItemChoicesComponent } from './student-slip-item-choices/student-slip-item-choices.component';
import { StudentSlipLoadingComponent } from './student-slip-loading/student-slip-loading.component';
import { PaymentState, PaymentStatus, StudentSlipModalStore, ViewModel } from './student-slip-modal.store';
import { StudentSlipPaymentComponent } from './student-slip-payment/student-slip-payment.component';
import { StudentSlipQuestionsComponent } from './student-slip-questions/student-slip-questions.component';

@Component({
  selector: 'app-student-slip-modal',
  templateUrl: './student-slip-modal.component.html',
  styleUrls: ['./student-slip-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StudentSlipModalStore],
  imports: [
    ModalBackButtonComponent,
    StudentSlipLoadingComponent,
    StudentSlipDetailsComponent,
    StudentSlipConsentComponent,
    StudentSlipQuestionsComponent,
    StudentSlipPaymentComponent,
    StudentSlipItemChoicesComponent,
    AsyncPipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonContent,
    IonGrid,
    IonButton
  ]
})
export class StudentSlipModalComponent implements OnInit {
  @Input() schoolId: number;
  @Input() studentId: number;
  @Input() studentForename: string;
  @Input() slipId: number;
  @Input() slip: Slip;

  PaymentState = PaymentState;

  readonly vm$: Observable<ViewModel> = this.store.vm$;
  readonly student$: Observable<Student> = this.store.student$;

  constructor(private store: StudentSlipModalStore) {}

  ngOnInit(): void {
    this.store.setSchoolId(this.schoolId);
    this.store.setStudentId(this.studentId);
    if (this.slip) {
      this.store.setSlip(this.slip);
      // We may have reopened a slip that is pending confirmation so need to prime it
      // in that state
      this.store.initialisePaymentStatus({ slipId: this.slip.id, studentId: this.studentId });
    } else {
      this.store.loadSlip({ id: this.slipId });
      // We may have reopened a slip that is pending confirmation so need to prime it
      // in that state
      this.store.initialisePaymentStatus({ slipId: this.slipId, studentId: this.studentId });
    }

    this.store.monitorIntentCheckSuccess();
  }

  onDismiss(): void {
    this.store.dismissModal();
  }

  onPaymentStatus(status: PaymentStatus): void {
    this.store.setPaymentStatus(status);
  }

  onPaymentMethodUpdated(methodId: string): void {
    this.store.updatePaymentMethod({ methodId: methodId });
  }

  onSubmitConsent(result: { accepted: boolean }): void {
    this.store.submitConsentResponse({ payload: result });
  }

  onReloadSlip(): void {
    this.store.loadSlip({ id: this.slip?.id ?? this.slipId });
  }

  onVisitAppStore(): void {
    this.store.visitAppStore();
  }

  onEditQuestions(): void {
    this.store.editQuestionResponses();
  }

  onViewQuestions(): void {
    this.store.viewQuestionResponses();
  }

  onEditItemChoices(): void {
    this.store.showItemChoicesModal({ readonly: false });
  }

  onViewItemChoices(): void {
    this.store.showItemChoicesModal({ readonly: true });
  }
}
